import usePrivateLink from "../../config/usePrivateLink";
import React, { useState, useEffect, useMemo } from "react";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Footer from "../footer";
import { Link } from "react-router-dom";
import TestLayout from "./TestLayout";

import {
  getTestSeriesList,
  testSeriesCreate,
  testSeriesDelete,
  testSeriesUpdate,
} from "../../../api/TestService";

import {
  ToastMain,
  closeModal,
  SweetAlertShow,
  LoadingDiv,
} from "../../master/Master";

import Swal from "sweetalert2";
import { getSessions } from "../../../api/SessionsService";

export default function Testlist() {
  const hasAccess = usePrivateLink();

  const [Data, setData] = useState([]);
  const [modalData, setModalData] = useState({
    id: "",
    title: "",
    type: "",
  });

  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sessionFilter, setSessionFilter] = useState("");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  const testSeries = Array.isArray(Data?.data) ? Data.data : [];

  const activeFilterCount = useMemo(() => {
    let count = 0;

    if (search.trim()) count++;
    if (sessionFilter) count++;

    return count;
  }, [search, sessionFilter]);

  const fetchSeriesList = async () => {
    setLoading(true);

    let addUrl = `?search=${encodeURIComponent(search)}`;

    if (sessionFilter) {
      addUrl += `&session_id=${sessionFilter}`;
    }

    try {
      const response = await getTestSeriesList(addUrl);

      if (response?.data?.status) {
        setData(response?.data?.data);
      } else {
        setData([]);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSearch("");
    setSessionFilter("");

    // Directly reload without depending on state update
    setTimeout(() => {
      fetchSeriesList();
    }, 0);
  };

  const getSession = async () => {
    try {
      const response = await getSessions();

      if (response?.data?.status) {
        setSessions(response?.data?.data || []);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load sessions.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong!",
      );
    }
  };

  const handleSeriesDelete = async (id) => {
    setLoading(true);

    try {
      const response = await testSeriesDelete(id);

      if (response?.data?.status) {
        await fetchSeriesList();

        ToastMain.fire({
          icon: "success",
          title: "Deleted successfully!",
        });
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to delete test series.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  const createSeries = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const formData = new FormData(e.target);

      const response = await testSeriesCreate(formData);

      if (response?.data?.status) {
        closeModal("Addnew");

        e.target.reset();

        await fetchSeriesList();

        ToastMain.fire({
          icon: "success",
          title: "Test series created successfully!",
        });
      } else {
        if (response?.data?.errors) {
          setError(response.data.errors);
        }

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Please check the entered details.",
        );
      }
    } catch (error) {
      if (error?.response?.data?.errors) {
        setError(error.response.data.errors);
      }

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  const TestseriesUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const formData = new FormData(e.target);

      const response = await testSeriesUpdate(formData);

      if (response?.data?.status) {
        closeModal("Updatenew");

        await fetchSeriesList();

        ToastMain.fire({
          icon: "success",
          title: "Test series updated successfully!",
        });
      } else {
        if (response?.data?.errors) {
          setError(response.data.errors);
        }

        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Please check the entered details.",
        );
      }
    } catch (error) {
      if (error?.response?.data?.errors) {
        setError(error.response.data.errors);
      }

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSession();
  }, []);

  useEffect(() => {
    fetchSeriesList();
  }, [search]);

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid py-3">
                <div className="row">
                  <TestLayout />

                  <div className="col-12">
                    {/* =====================================================
                        PAGE HEADER
                    ====================================================== */}
                    <div className="card border-0 shadow-sm mb-4">
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="bg-label-primary rounded-3 d-flex align-items-center justify-content-center"
                              style={{
                                width: "52px",
                                height: "52px",
                              }}
                            >
                              <i className="ri-file-list-3-line fs-3 text-primary" />
                            </div>

                            <div>
                              <h4 className="mb-1 fw-bold">Test Series</h4>

                              <p className="text-muted mb-0 small">
                                Create, manage and organize your tests and
                                examinations.
                              </p>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            {/* Filter */}
                            <button
                              type="button"
                              className={`btn ${
                                activeFilterCount
                                  ? "btn-primary"
                                  : "btn-outline-primary"
                              }`}
                              data-bs-toggle="offcanvas"
                              data-bs-target="#filterCanvas"
                            >
                              <i className="ri-filter-3-line me-1" />
                              Filters
                              {activeFilterCount > 0 && (
                                <span className="badge rounded-pill bg-white text-primary ms-2">
                                  {activeFilterCount}
                                </span>
                              )}
                            </button>

                            {/* Add */}
                            {hasAccess("/tests/add") && (
                              <button
                                type="button"
                                className="btn btn-primary"
                                data-bs-toggle="modal"
                                data-bs-target="#Addnew"
                                onClick={() => {
                                  setError("");
                                  setModalData({
                                    id: "",
                                    title: "",
                                    type: "",
                                  });
                                }}
                              >
                                <i className="ri-add-line me-1" />
                                New Test Series
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Quick stats */}
                        <div className="row g-3 mt-3">
                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="bg-label-primary rounded-3 p-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <small className="text-muted d-block mb-1">
                                    Total Series
                                  </small>

                                  <h4 className="mb-0 fw-bold">
                                    {testSeries.length}
                                  </h4>
                                </div>

                                <i className="ri-file-list-3-line fs-3 text-primary" />
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="bg-label-success rounded-3 p-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <small className="text-muted d-block mb-1">
                                    Tests
                                  </small>

                                  <h4 className="mb-0 fw-bold">
                                    {
                                      testSeries.filter(
                                        (item) => item.type === "0",
                                      ).length
                                    }
                                  </h4>
                                </div>

                                <i className="ri-file-edit-line fs-3 text-success" />
                              </div>
                            </div>
                          </div>

                          <div className="col-12 col-sm-6 col-md-4">
                            <div className="bg-label-warning rounded-3 p-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <small className="text-muted d-block mb-1">
                                    Examinations
                                  </small>

                                  <h4 className="mb-0 fw-bold">
                                    {
                                      testSeries.filter(
                                        (item) => item.type === "1",
                                      ).length
                                    }
                                  </h4>
                                </div>

                                <i className="ri-medal-line fs-3 text-warning" />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* =====================================================
                        LIST CARD
                    ====================================================== */}
                    <div className="card border-0 shadow-sm">
                      <div className="card-body p-0">
                        {/* Table Header */}
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-4 py-3 border-bottom">
                          <div>
                            <h5 className="mb-1 fw-semibold">
                              All Test Series
                            </h5>

                            <small className="text-muted">
                              {testSeries.length} series found
                            </small>
                          </div>

                          {activeFilterCount > 0 && (
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-label-primary">
                                <i className="ri-filter-line me-1" />
                                {activeFilterCount} filter
                                {activeFilterCount > 1 ? "s" : ""} active
                              </span>

                              <button
                                type="button"
                                className="btn btn-sm btn-text-secondary"
                                onClick={handleReset}
                              >
                                Clear
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Table */}
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead>
                              <tr>
                                <th
                                  className="ps-4 text-muted fw-semibold"
                                  style={{ width: "80px" }}
                                >
                                  #
                                </th>

                                <th className="text-muted fw-semibold">
                                  Test Series
                                </th>

                                <th
                                  className="text-muted fw-semibold"
                                  style={{ width: "180px" }}
                                >
                                  Type
                                </th>

                                <th
                                  className="text-muted fw-semibold text-end pe-4"
                                  style={{ width: "150px" }}
                                >
                                  Actions
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {testSeries.length > 0 ? (
                                testSeries.map((item, index) => (
                                  <tr key={item.id || index}>
                                    {/* Sr No */}
                                    <td className="ps-4">
                                      <span className="text-muted fw-medium">
                                        {String(index + 1).padStart(2, "0")}
                                      </span>
                                    </td>

                                    {/* Name */}
                                    <td>
                                      <div className="d-flex align-items-center">
                                        <div className="avatar avatar-sm me-3">
                                          <span className="avatar-initial rounded bg-label-primary">
                                            <i className="ri-file-list-3-line" />
                                          </span>
                                        </div>

                                        <div>
                                          <h6 className="mb-0 fw-semibold">
                                            {item.title}
                                          </h6>
                                        </div>
                                      </div>
                                    </td>

                                    {/* Type */}
                                    <td>
                                      {item.type === "0" ? (
                                        <span className="badge bg-label-primary rounded-pill px-3 py-2">
                                          <i className="ri-file-edit-line me-1" />
                                          Test
                                        </span>
                                      ) : (
                                        <span className="badge bg-label-warning rounded-pill px-3 py-2">
                                          <i className="ri-medal-line me-1" />
                                          Exam
                                        </span>
                                      )}
                                    </td>

                                    {/* Actions */}
                                    <td className="text-end pe-4">
                                      <div className="d-flex justify-content-end gap-2">
                                        {hasAccess("/tests/edit") && (
                                          <Link
                                            to=""
                                            state={{ id: item.id }}
                                            data-bs-toggle="modal"
                                            data-bs-target="#Updatenew"
                                            className="btn btn-sm btn-icon btn-outline-primary"
                                            title="Edit"
                                            onClick={() => {
                                              setError("");
                                              setModalData({
                                                ...item,
                                              });
                                            }}
                                          >
                                            <i className="ri-pencil-line" />
                                          </Link>
                                        )}

                                        {hasAccess("/tests/delete") && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-icon btn-outline-danger"
                                            title="Delete"
                                            onClick={() => {
                                              Swal.fire({
                                                title: "Delete test series?",
                                                text: `Are you sure you want to delete "${item.title}"? This action cannot be undone.`,
                                                icon: "warning",
                                                showCancelButton: true,
                                                confirmButtonColor: "#d33",
                                                cancelButtonColor: "#6c757d",
                                                confirmButtonText:
                                                  "Yes, delete it",
                                                cancelButtonText: "Cancel",
                                              }).then((result) => {
                                                if (result.isConfirmed) {
                                                  handleSeriesDelete(item.id);
                                                }
                                              });
                                            }}
                                          >
                                            <i className="ri-delete-bin-line" />
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={4}>
                                    <div className="text-center py-5 px-3">
                                      <div
                                        className="bg-label-primary rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                                        style={{
                                          width: "72px",
                                          height: "72px",
                                        }}
                                      >
                                        <i className="ri-file-search-line fs-1 text-primary" />
                                      </div>

                                      <h5 className="fw-semibold mb-2">
                                        No Test Series Found
                                      </h5>

                                      <p className="text-muted mb-4 mx-auto">
                                        {activeFilterCount > 0
                                          ? "No test series match your current filters. Try changing the search or session."
                                          : "You haven't created any test series yet."}
                                      </p>

                                      {activeFilterCount > 0 ? (
                                        <button
                                          type="button"
                                          className="btn btn-outline-primary"
                                          onClick={handleReset}
                                        >
                                          <i className="ri-refresh-line me-1" />
                                          Clear Filters
                                        </button>
                                      ) : (
                                        hasAccess("/tests/add") && (
                                          <button
                                            type="button"
                                            className="btn btn-primary"
                                            data-bs-toggle="modal"
                                            data-bs-target="#Addnew"
                                          >
                                            <i className="ri-add-line me-1" />
                                            Create Test Series
                                          </button>
                                        )
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Footer />

            <div className="content-backdrop fade" />
          </div>
        </div>

        <div className="layout-overlay layout-menu-toggle"></div>
        <div className="drag-target"></div>
      </div>

      {/* =========================================================
          FILTER OFFCANVAS
      ========================================================== */}
      <div
        className="offcanvas offcanvas-end"
        tabIndex="-1"
        id="filterCanvas"
        aria-labelledby="filterCanvasLabel"
      >
        <div className="offcanvas-header border-bottom">
          <div>
            <h5 className="mb-1 fw-bold" id="filterCanvasLabel">
              <i className="ri-filter-3-line text-primary me-2" />
              Filter Test Series
            </h5>

            <small className="text-muted">
              Refine the list using the options below.
            </small>
          </div>

          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          />
        </div>

        <div className="offcanvas-body">
          {/* Search */}
          <div className="mb-4">
            <label className="form-label fw-semibold">Search Test Series</label>

            <div className="input-group">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>

              <input
                type="text"
                className="form-control"
                placeholder="Search by series name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Session */}
          <div className="mb-4">
            <label className="form-label fw-semibold">Academic Session</label>

            <select
              className="form-select"
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
            >
              <option value="">All Sessions</option>

              {sessions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.session}
                </option>
              ))}
            </select>
          </div>

          {/* Active Filters */}
          {activeFilterCount > 0 && (
            <div className="bg-label-primary rounded-3 p-3">
              <div className="d-flex align-items-center mb-2">
                <i className="ri-information-line me-2 text-primary" />

                <span className="fw-semibold">Active Filters</span>
              </div>

              <div className="d-flex flex-wrap gap-2">
                {search.trim() && (
                  <span className="badge bg-primary">Search: {search}</span>
                )}

                {sessionFilter && (
                  <span className="badge bg-primary">
                    Session:{" "}
                    {
                      sessions.find(
                        (item) => String(item.id) === String(sessionFilter),
                      )?.session
                    }
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-top p-3">
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-light flex-fill"
              onClick={handleReset}
              data-bs-dismiss="offcanvas"
            >
              <i className="ri-refresh-line me-1" />
              Reset
            </button>

            <button
              type="button"
              className="btn btn-primary flex-fill"
              data-bs-dismiss="offcanvas"
              onClick={fetchSeriesList}
            >
              <i className="ri-search-line me-1" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* =========================================================
          ADD TEST SERIES MODAL
      ========================================================== */}
      <div className="modal fade" id="Addnew" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow">
            <form onSubmit={createSeries}>
              <div className="modal-header border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <div className="avatar">
                    <span className="avatar-initial rounded bg-label-primary">
                      <i className="ri-add-line fs-5" />
                    </span>
                  </div>

                  <div>
                    <h5 className="modal-title fw-bold mb-1">
                      Add Test Series
                    </h5>

                    <small className="text-muted">
                      Create a new test or examination series.
                    </small>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>

              <div className="modal-body p-4">
                {/* Name */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Test Series Name <span className="text-danger">*</span>
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="ri-file-list-3-line" />
                    </span>

                    <input
                      type="text"
                      name="title"
                      placeholder="Enter test series name"
                      className="form-control input-capitalize"
                      required
                    />
                  </div>

                  {error?.title && (
                    <small className="text-danger d-block mt-1">
                      {error.title}
                    </small>
                  )}
                </div>

                {/* Type */}
                <div className="mb-2">
                  <label className="form-label fw-semibold">
                    Series Type <span className="text-danger">*</span>
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="ri-file-edit-line" />
                    </span>

                    <select name="type" className="form-select" required>
                      <option value="">Select series type</option>
                      <option value="0">Test</option>
                      <option value="1">Exam</option>
                    </select>
                  </div>

                  {error?.type && (
                    <small className="text-danger d-block mt-1">
                      {error.type}
                    </small>
                  )}
                </div>
              </div>

              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line me-1" />
                      Create Series
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* =========================================================
          UPDATE TEST SERIES MODAL
      ========================================================== */}
      <div
        className="modal fade"
        id="Updatenew"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content border-0 shadow">
            <form onSubmit={TestseriesUp}>
              <div className="modal-header border-bottom">
                <div className="d-flex align-items-center gap-3">
                  <div className="avatar">
                    <span className="avatar-initial rounded bg-label-primary">
                      <i className="ri-pencil-line fs-5" />
                    </span>
                  </div>

                  <div>
                    <h5 className="modal-title fw-bold mb-1">
                      Update Test Series
                    </h5>

                    <small className="text-muted">
                      Modify the test series details.
                    </small>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>

              <div className="modal-body p-4">
                <input type="hidden" name="id" value={modalData.id} />

                {/* Name */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    Test Series Name
                  </label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="ri-file-list-3-line" />
                    </span>

                    <input
                      type="text"
                      name="title"
                      className="form-control"
                      value={modalData?.title || ""}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>

                  {error?.title && (
                    <small className="text-danger d-block mt-1">
                      {error.title}
                    </small>
                  )}
                </div>

                {/* Type */}
                <div className="mb-2">
                  <label className="form-label fw-semibold">Series Type</label>

                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="ri-file-edit-line" />
                    </span>

                    <select
                      name="type"
                      className="form-select"
                      value={modalData?.type ?? ""}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          type: e.target.value,
                        }))
                      }
                      required
                    >
                      <option value="0">Test</option>
                      <option value="1">Exam</option>
                    </select>
                  </div>

                  {error?.type && (
                    <small className="text-danger d-block mt-1">
                      {error.type}
                    </small>
                  )}
                </div>
              </div>

              <div className="modal-footer border-top">
                <button
                  type="button"
                  className="btn btn-light"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
